import { AlertCircle, HelpCircle } from "@icons/untitled_ui_icons";
import { Tooltip } from "@src/components/ui-components/Tooltip/Tooltip";
import clsx from "clsx";
import { FieldError, Input, Label, Text, TextField, type TextFieldProps, type ValidationResult } from "react-aria-components";
import Skeleton from "react-loading-skeleton";
import TypographyText from "../../TypographyText/TypographyText";
import styles from "./TextInput.module.css";

export interface TextInputProps extends TextFieldProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  size?: "xs" | "sm" | "md";
  iconBefore?: React.ElementType; //example: iconBefore={Key}
  helpIcon?: boolean;
  helpText?: string;
  isLoading?: boolean;
  variant?: "default" | "borderless";
  rightText?: string | null | undefined;
}
export function TextInput({
  label,
  description,
  errorMessage,
  placeholder,
  size = "sm",
  iconBefore: IconBefore,
  helpIcon,
  helpText,
  variant = "default",
  rightText,
  ...props
}: TextInputProps) {
  const sizeMapPadding = {
    xs:
      IconBefore && (props.isInvalid || helpIcon)
        ? "5px 30px 5px 42px"
        : IconBefore && !props.isInvalid && !helpIcon
          ? "5px 11px 5px 42px"
          : !IconBefore && (props.isInvalid || helpIcon)
            ? "5px 30px 5px 11px"
            : "5px 11px",
    sm:
      IconBefore && (props.isInvalid || helpIcon)
        ? "7px 30px 7px 42px"
        : IconBefore && !props.isInvalid && !helpIcon
          ? "7px 11px 7px 42px"
          : !IconBefore && (props.isInvalid || helpIcon)
            ? "7px 30px 7px 11px"
            : "7px 11px",
    md:
      IconBefore && (props.isInvalid || helpIcon)
        ? "9px 30px 9px 42px"
        : IconBefore && !props.isInvalid && !helpIcon
          ? "9px 13px 9px 42px"
          : !IconBefore && (props.isInvalid || helpIcon)
            ? "9px 30px 9px 13px"
            : "9px 13px",
  };

  const sizeMapPaddingSkeleton = {
    xs: "5px 0",
    sm: "7px 0",
    md: "9px 0",
  };
  if (props.isLoading) {
    return (
      <TextField {...props} className={styles.reactAriaTextField + " " + props.className} aria-label={label ?? "text field"}>
        {label && <Skeleton width="100%" className={styles.reactAriaLabel} />}
        <Skeleton height={26} width="100%" style={{ padding: sizeMapPaddingSkeleton[size] }} />
        {description && <Skeleton width="100%" className={styles.description} />}
      </TextField>
    );
  }
  return (
    <TextField {...props} className={clsx(styles.reactAriaTextField, props.className)} aria-label={label ?? "text field"}>
      {label && <Label className={styles.reactAriaLabel}>{label}</Label>}
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", position: "relative", width: "100%" }}>
        {IconBefore && <IconBefore width={20} height={20} color="var(--gray-500)" style={{ position: "absolute", left: "14px" }} />}
        <Input
          className={clsx(styles.reactAriaInput, variant === "borderless" && styles.borderless)}
          placeholder={placeholder}
          style={{ padding: sizeMapPadding[size], width: "100%", fontSize: "14px" }}
        />
        <div style={{ position: "absolute", right: "14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {rightText && (
            <TypographyText size="sm" weight="regular" color="var(--gray-600)">
              {rightText}
            </TypographyText>
          )}
          {props.isInvalid && <AlertCircle width={16} height={16} color="var(--error-500)" />}
          {helpIcon && !props.isInvalid && (
            <Tooltip
              insideToolTip={
                <TypographyText size="sm" weight="regular">
                  {helpText}
                </TypographyText>
              }
              triggerElement={<HelpCircle width={16} height={16} color="var(--gray-500)" />}
            />
          )}
        </div>
      </div>
      {description && (
        <Text className={styles.description} slot="description">
          {description}
        </Text>
      )}
      {errorMessage && <FieldError className={styles.reactAriaFieldError}>{errorMessage}</FieldError>}
    </TextField>
  );
}
